import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { getLaunchFlags } from '../callables';
import { useAuth, useEnsureAuth } from '../firebase/FirebaseContext';
import { useAuthSnapshot } from '../hooks/useAuthSnapshot';

/**
 * Trebuchet: the selective-launch flag fetch, done ONCE per auth state.
 *
 * The server owns every targeting rule (uid allow/deny lists, percentage
 * rollout, internal-only, requireSignedIn) and hands the client booleans only
 * — `getLaunchFlags` returns the evaluated answer for THIS caller, so nothing
 * here decides anything. This provider's whole job is to hold that answer and
 * keep it honest across the auth transitions that change who "this caller" is.
 *
 * The flag map is `null` until the fetch settles — that is the `pending` state
 * `useTrebuchet` surfaces, and it is a first-class value, never a synonym for
 * "off" (the THE-664 lesson; see `useTrebuchet.ts` and
 * `src/theaWeb/hub/giftGuideHubFlag.ts`).
 *
 * FAIL CLOSED, BUT SETTLED: a failed call resolves the map to `{}`, so every
 * flag reads `'disabled'` rather than the tree hanging on `'pending'`. There
 * is deliberately no retry loop, no onSnapshot, and no localStorage echo —
 * one fetch per auth state is the contract, and the server-side gates stay
 * authoritative for anything that happens mid-session.
 */

/** `null` = fetch not settled for the current uid ('pending' to consumers). */
type TrebuchetFlagMap = Record<string, boolean> | null;

/** `undefined` = no provider mounted (useTrebuchet fails closed on it). */
const TrebuchetContext = createContext<TrebuchetFlagMap | undefined>(undefined);

/** The raw evaluated map — consumers want `useTrebuchet`, not this. */
export function useTrebuchetFlagMap(): TrebuchetFlagMap | undefined {
  return useContext(TrebuchetContext);
}

export interface TrebuchetProviderProps {
  children: ReactNode;
}

export const TrebuchetProvider: React.FC<TrebuchetProviderProps> = ({ children }) => {
  const auth = useAuth();
  const ensureAuth = useEnsureAuth();
  // Reactive uid: merge/claim flows mint NEW uids and the server evaluates
  // flags per uid, so the fetch must follow a uid swap. linkWithCredential /
  // linkWithPopup upgrade anon→permanent IN PLACE (same uid), so a normal
  // signup does NOT refetch — the uid the server answered for hasn't changed.
  const { uid } = useAuthSnapshot(auth);
  const [flags, setFlags] = useState<TrebuchetFlagMap>(null);
  // The uid a fetch was ISSUED for. Dedupes the cold-load double fire: the
  // effect runs once at uid=null, ensureAuth mints the anon uid, and the
  // snapshot's null→uid flip runs it again for the same uid.
  const fetchedForUid = useRef<string | null>(null);
  // Stale-response guard, by SEQUENCE rather than by effect scope (the
  // useHolidayPrefs scope-ref variant): only the latest-issued fetch may
  // commit. An effect-scoped guard deadlocks here — on cold load the uid flip
  // re-runs the effect, whose cleanup would invalidate the in-flight fetch
  // that fetchedForUid already recorded, dropping the one response the dedupe
  // then prevents anyone from re-requesting.
  const fetchSeq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      let authedUid: string;
      try {
        authedUid = await ensureAuth();
      } catch {
        // Auth never settled: fail closed as a SETTLED answer. A map that
        // stayed pending here would hold every gated consumer on its loading
        // state forever (the trap giftGuideHubFlag's release read documents).
        if (mounted.current) setFlags({});
        return;
      }
      if (!mounted.current || fetchedForUid.current === authedUid) return;
      const isRefetch = fetchedForUid.current !== null;
      fetchedForUid.current = authedUid;
      const seq = ++fetchSeq.current;
      // A NEW uid's answers are unknown — back to pending rather than serving
      // the previous uid's map for even a render.
      if (isRefetch) setFlags(null);
      try {
        const result = await getLaunchFlags();
        if (!mounted.current || fetchSeq.current !== seq) return;
        setFlags(result.data.flags ?? {});
      } catch {
        // Call failure: everything off, but settled (fail closed).
        if (!mounted.current || fetchSeq.current !== seq) return;
        setFlags({});
      }
    })();
  }, [ensureAuth, uid]);

  // Demo consumer proving the end-to-end path (reviewer-visible in devtools,
  // deliberately no UI): the same three-state read useTrebuchet derives.
  useEffect(() => {
    console.debug(
      '[trebuchet] demo flag',
      flags === null ? 'pending' : flags.trebuchetDemo === true ? 'enabled' : 'disabled',
    );
  }, [flags]);

  return <TrebuchetContext.Provider value={flags}>{children}</TrebuchetContext.Provider>;
};
